/**
 * The two per-client actions a firm with a team needs: hand this client to another adviser, and
 * decide who can see them.
 *
 * They live behind a ⋯ on the client row because the Actions column already has two buttons and
 * neither of these is an everyday one. And the ⋯ appears only when there is something inside it —
 * each item has its own condition, not a shared "has a team" one:
 *
 *   Reassign      — only once somebody else can hold clients. With nobody to reassign TO it is a
 *                   dead menu item.
 *   Who can see   — only once the firm has support. Advisers already see just their own clients and
 *                   the principal always sees everything, so with no support there is nobody the
 *                   switch could exclude.
 *
 * So a sole practitioner gets the Actions column exactly as it is today, and two advisers with no
 * support get Reassign and nothing else.
 *
 * It is deliberately NOT called "access". The row already has an Access Mode column meaning
 * something completely different — how far the CLIENT gets on their own. Two "access" controls on
 * one row, meaning opposite things, is a support call waiting to happen.
 */
import React, { useEffect, useState } from 'react';
import { listFirmMembers, reassignClient, setClientVisibility, type FirmMember } from '../../services/tenantApi.ts';

interface ClientRowActionsProps {
    profileId: string;
    clientName: string;
    adviserUserId: string | null;
    visibility: 'firm' | 'restricted';
    tenantId: string;
    myUserId: string;
    isPrincipal: boolean;
    /** Re-read the client list after a change — the row's adviser or badge has moved. */
    onChanged?: () => void;
}

const menuStyle = {
    position: 'absolute',
    right: 0,
    top: '100%',
    zIndex: 40,
    minWidth: 230,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 28px -8px rgba(15,23,42,.28)',
    padding: 6,
    marginTop: 4,
} as const;

const itemStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 0,
    borderRadius: 6,
    padding: '9px 11px',
    fontSize: 13,
    color: '#334155',
    cursor: 'pointer',
} as const;

const dialogWrap = {
    position: 'fixed',
    inset: 0,
    zIndex: 60,
    background: 'rgba(15,23,42,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
} as const;

const dialog = { background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', padding: 22, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto' } as const;
const primaryBtn = { background: '#1a6fd4', color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' } as const;
const ghostBtn = { background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' } as const;


/** The rows' shared read of the firm's roster: one request per firm, reused for half a minute. */
const ROSTER_REUSE_MS = 30_000;
const rosterReads = new Map<string, { at: number; members: Promise<FirmMember[]> }>();
export function sharedFirmMembers(tenantId: string, now = Date.now()): Promise<FirmMember[]> {
    const hit = rosterReads.get(tenantId);
    if (hit && now - hit.at < ROSTER_REUSE_MS) return hit.members;
    const members = listFirmMembers(tenantId);
    rosterReads.set(tenantId, { at: now, members });
    // A failed read is not reused.
    members.catch(() => { if (rosterReads.get(tenantId)?.members === members) rosterReads.delete(tenantId); });
    return members;
}

export function ClientRowActions({
    profileId,
    clientName,
    adviserUserId,
    visibility,
    tenantId,
    myUserId,
    isPrincipal,
    onChanged,
}: ClientRowActionsProps) {
    const [members, setMembers] = useState<FirmMember[] | null>(null);
    const [open, setOpen] = useState(false);
    const [dialogKind, setDialogKind] = useState<'reassign' | 'visibility' | null>(null);
    const [choice, setChoice] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // One roster read shared by every row (26 Oct 2026: a dashboard of 24 clients sent 25 identical reads),
    // still self-contained rather than threaded through the whole portal for a menu most firms never open.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const list = await sharedFirmMembers(tenantId);
                if (!cancelled) setMembers(list);
            } catch {
                // A roster we cannot read means no menu, which is the honest outcome — better than a
                // menu whose actions would fail.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    if (!members) return null;

    const holders = members.filter((m) => (m.role === 'owner' || m.role === 'adviser') && m.status === 'active');
    const hasSupport = members.some((m) => m.role === 'staff' && m.status === 'active');
    const mine = adviserUserId === myUserId;
    // Only the principal or the responsible adviser may do either of these — the server enforces it,
    // and offering a button that always fails is worse than not offering it.
    const mayAct = isPrincipal || mine;

    const canReassign = mayAct && holders.length > 1;
    const canSetVisibility = mayAct && hasSupport;
    if (!canReassign && !canSetVisibility) return null;

    const close = () => {
        setDialogKind(null);
        setError(null);
        setBusy(false);
    };

    const submit = async () => {
        setBusy(true);
        setError(null);
        const res =
            dialogKind === 'reassign'
                ? await reassignClient(profileId, choice)
                : await setClientVisibility(profileId, choice === 'restricted' ? 'restricted' : 'firm', note.trim() || null);
        setBusy(false);
        if (!res.ok) {
            setError(res.message ?? 'That did not work.');
            return;
        }
        close();
        onChanged?.();
    };

    return (
        <span style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                className="ab"
                aria-label={`More actions for ${clientName}`}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                ⋯
            </button>

            {open && (
                <>
                    {/* Click-away without a document listener: a transparent sheet behind the menu. */}
                    <span
                        role="presentation"
                        onClick={() => setOpen(false)}
                        style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                    />
                    <span style={menuStyle}>
                        {canReassign && (
                            <button
                                type="button"
                                style={itemStyle}
                                onClick={() => {
                                    setOpen(false);
                                    setChoice(adviserUserId ?? '');
                                    setDialogKind('reassign');
                                }}
                            >
                                Reassign to another adviser…
                            </button>
                        )}
                        {canSetVisibility && (
                            <button
                                type="button"
                                style={itemStyle}
                                onClick={() => {
                                    setOpen(false);
                                    setChoice(visibility);
                                    setNote('');
                                    setDialogKind('visibility');
                                }}
                            >
                                Who can see this…
                            </button>
                        )}
                    </span>
                </>
            )}

            {dialogKind && (
                <span style={dialogWrap} role="presentation" onClick={(e) => e.target === e.currentTarget && close()}>
                    <span style={dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 3 }}>
                            {dialogKind === 'reassign' ? 'Reassign this client' : 'Who can see this file?'}
                        </div>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>{clientName}</p>

                        {dialogKind === 'reassign' ? (
                            <>
                                <div style={{ display: 'grid', gap: 8 }}>
                                    {holders.map((m) => (
                                        <button
                                            key={m.userId}
                                            type="button"
                                            onClick={() => setChoice(m.userId)}
                                            style={{
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                background: choice === m.userId ? '#f0f7ff' : '#fff',
                                                border: choice === m.userId ? '2px solid #1a6fd4' : '1px solid #e2e8f0',
                                                borderRadius: 10,
                                                padding: choice === m.userId ? 10 : 11,
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a' }}>
                                                {m.email}
                                                {m.userId === adviserUserId && (
                                                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: '#64748b' }}>holds it now</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b' }}>
                                                {m.role === 'owner' ? 'Principal' : 'Adviser'} · {m.clientCount} on their book
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0', lineHeight: 1.5 }}>
                                    Nobody is charged again — this client was taken on once, and moving them between
                                    advisers in your firm is not a second client.
                                </p>
                            </>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gap: 8 }}>
                                    {[
                                        { value: 'firm', title: 'Everyone in the practice', detail: 'The default — support can help with this client.' },
                                        { value: 'restricted', title: 'The responsible adviser and the principal only', detail: 'Support will not see this client at all.' },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setChoice(opt.value)}
                                            style={{
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                background: choice === opt.value ? '#f0f7ff' : '#fff',
                                                border: choice === opt.value ? '2px solid #1a6fd4' : '1px solid #e2e8f0',
                                                borderRadius: 10,
                                                padding: choice === opt.value ? 10 : 11,
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a' }}>{opt.title}</div>
                                            <div style={{ fontSize: 12, color: '#64748b' }}>{opt.detail}</div>
                                        </button>
                                    ))}
                                </div>

                                {choice === 'restricted' && (
                                    <div style={{ marginTop: 12 }}>
                                        <label
                                            htmlFor="visibility-note"
                                            style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 6 }}
                                        >
                                            Why — optional, seen by the principal
                                        </label>
                                        <input
                                            id="visibility-note"
                                            style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14 }}
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            placeholder="e.g. a colleague's family"
                                        />
                                    </div>
                                )}

                                <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0', lineHeight: 1.5 }}>
                                    This is forward-looking. Restricting a file somebody has already read does not unread
                                    it — for the cases you can see coming, set it when the file is created.
                                </p>
                            </>
                        )}

                        {error && (
                            <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '9px 12px', fontSize: 13 }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
                            <button type="button" style={ghostBtn} onClick={close}>
                                Cancel
                            </button>
                            <button type="button" style={primaryBtn} disabled={busy || !choice} onClick={() => void submit()}>
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </span>
                </span>
            )}
        </span>
    );
}
